import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const PreSelectWorkspaceDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PreSelectWorkspace"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"member"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"confirmed"}},{"kind":"Field","name":{"kind":"Name","value":"enterpriseLicenses"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"idEnterprise"}},{"kind":"Field","name":{"kind":"Name","value":"type"}}]}},{"kind":"Field","name":{"kind":"Name","value":"idEnterprise"}},{"kind":"Field","name":{"kind":"Name","value":"organizations"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"limits"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"orgs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"freeBoardsPerOrg"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"count"}},{"kind":"Field","name":{"kind":"Name","value":"disableAt"}}]}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"offering"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PreSelectWorkspace","document":PreSelectWorkspaceDocument}} as const;
export type PreSelectWorkspaceQueryVariables = Types.Exact<{
  memberId: Types.Scalars['ID']['input'];
}>;


export type PreSelectWorkspaceQuery = (
  { __typename: 'Query' }
  & { member?: Types.Maybe<(
    { __typename: 'Member' }
    & Pick<Types.Member, 'id' | 'confirmed' | 'idEnterprise'>
    & { enterpriseLicenses?: Types.Maybe<Array<(
      { __typename: 'Member_EnterpriseLicense' }
      & Pick<Types.Member_EnterpriseLicense, 'idEnterprise' | 'type'>
    )>>, organizations: Array<(
      { __typename: 'Organization' }
      & Pick<Types.Organization, 'id' | 'name' | 'offering'>
      & { limits: (
        { __typename: 'Organization_Limits' }
        & { orgs: (
          { __typename: 'Organization_Limits_Orgs' }
          & { freeBoardsPerOrg: (
            { __typename: 'Limit' }
            & Pick<Types.Limit, 'count' | 'disableAt'>
          ) }
        ) }
      ) }
    )> }
  )> }
);

/**
 * __usePreSelectWorkspaceQuery__
 *
 * To run a query within a React component, call `usePreSelectWorkspaceQuery` and pass it any options that fit your needs.
 * When your component renders, `usePreSelectWorkspaceQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePreSelectWorkspaceQuery({
 *   variables: {
 *      memberId: // value for 'memberId'
 *   },
 * });
 */
export function usePreSelectWorkspaceQuery(
  baseOptions: TrelloQueryHookOptions<
    PreSelectWorkspaceQuery,
    PreSelectWorkspaceQueryVariables
  > &
    (
      | { variables: PreSelectWorkspaceQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PreSelectWorkspaceDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    PreSelectWorkspaceQuery,
    PreSelectWorkspaceQueryVariables
  >(PreSelectWorkspaceDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePreSelectWorkspaceLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PreSelectWorkspaceQuery,
    PreSelectWorkspaceQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    PreSelectWorkspaceQuery,
    PreSelectWorkspaceQueryVariables
  >(PreSelectWorkspaceDocument, options);
}
export function usePreSelectWorkspaceSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PreSelectWorkspaceQuery,
        PreSelectWorkspaceQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    PreSelectWorkspaceQuery,
    PreSelectWorkspaceQueryVariables
  >(PreSelectWorkspaceDocument, options);
}
export type PreSelectWorkspaceQueryHookResult = ReturnType<
  typeof usePreSelectWorkspaceQuery
>;
export type PreSelectWorkspaceLazyQueryHookResult = ReturnType<
  typeof usePreSelectWorkspaceLazyQuery
>;
export type PreSelectWorkspaceSuspenseQueryHookResult = ReturnType<
  typeof usePreSelectWorkspaceSuspenseQuery
>;
export type PreSelectWorkspaceQueryResult = Apollo.QueryResult<
  PreSelectWorkspaceQuery,
  PreSelectWorkspaceQueryVariables
>;
